import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatIDR } from "@/lib/format";
import { usePagination } from "@/hooks/usePagination";
import { Input, Select, Modal } from "@/components/ui-kit";
import { useCart } from "@/hooks/useCart";
import { toast } from "sonner";
import { Search, Package, MapPin, Tag, ShoppingBag, ArrowRight, Sparkles, Building2, Filter, X, Check, ShoppingCart, Minus, Plus } from "lucide-react";

/**
 * Iter v34aw — ClientCatalog refactored to Bento-Grid "Online Shop" layout.
 * Preserves all data (per-location variants), catalog_visible_fields per-field logic,
 * catalog_hidden filter, and detail modal semantics. View-only (no cart/checkout).
 */
export default function ClientCatalog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { count: cartCount, addItem } = useCart();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");
  const [sort, setSort] = useState("name");
  const [loading, setLoading] = useState(true);
  const [activePrice, setActivePrice] = useState(null);
  // Iter v34cj — Stok barang per material_name (map lookup)
  const [stockMap, setStockMap] = useState({});

  useEffect(() => {
    setLoading(true);
    // Iter v34b5 — Endpoint ringan `/catalog` (filter catalog_hidden + exclude image_b64).
    // Fallback ke `/prices` bila endpoint baru belum tersedia (production yg belum redeploy).
    api.get("/catalog").then((r) => {
      setRows(r.data || []);
      setLoading(false);
    }).catch(() => {
      api.get("/prices").then((r) => {
        const visible = (r.data || []).filter((p) => !p.catalog_hidden);
        setRows(visible);
        setLoading(false);
      }).catch(() => setLoading(false));
    });
    // Iter v34cj — Load stok barang untuk display per katalog
    api.get("/catalog-stocks").then((r) => {
      const m = {};
      (r.data || []).forEach((s) => {
        if (s.material_name) m[s.material_name.toLowerCase()] = s;
      });
      setStockMap(m);
    }).catch(() => {});
  }, []);

  const categories = useMemo(() => Array.from(new Set(rows.map((r) => r.category).filter(Boolean))).sort(), [rows]);
  const locations = useMemo(() => Array.from(new Set(rows.map((r) => r.delivery_location).filter(Boolean))).sort(), [rows]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = (r.material_name || "—").toLowerCase().trim();
      if (!map.has(key)) {
        map.set(key, {
          key,
          material_name: r.material_name,
          category: r.category,
          description: r.description,
          // Iter v34b5 — Ganti image_b64 heavy → has_image + image_variant_id.
          // Gambar di-fetch lazy per-item via /prices/{id}/image.
          has_image: !!r.has_image,
          image_variant_id: r.has_image ? r.id : null,
          unit: r.unit,
          variants: [],
        });
      } else {
        const g = map.get(key);
        if (!g.has_image && r.has_image) {
          g.has_image = true;
          g.image_variant_id = r.id;
        }
      }
      map.get(key).variants.push(r);
    }
    return Array.from(map.values()).map((g) => {
      // Use lowest visible price across variants for "from" display
      const prices = g.variants.map((v) => {
        const vf = Array.isArray(v.catalog_visible_fields) ? v.catalog_visible_fields : [];
        const showAll = vf.length === 0;
        const show = (k) => showAll || vf.includes(k);
        return (show("price_with_vat") && v.price_with_vat)
          || (show("price_ex_vat") && v.price_ex_vat)
          || (show("price_non_vat") && v.price_non_vat)
          || 0;
      }).filter((p) => p > 0);
      const minPrice = prices.length ? Math.min(...prices) : 0;
      const locs = Array.from(new Set(g.variants.map((v) => v.delivery_location).filter(Boolean)));
      return { ...g, min_price: minPrice, locations: locs };
    });
  }, [rows]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    let out = grouped.filter((g) => {
      if (s && !`${g.material_name || ""} ${g.unit || ""} ${g.locations.join(" ")} ${g.category || ""}`.toLowerCase().includes(s)) return false;
      if (category && g.category !== category) return false;
      if (location && !g.locations.includes(location)) return false;
      return true;
    });
    if (sort === "price-asc") out = out.slice().sort((a, b) => a.min_price - b.min_price);
    else if (sort === "price-desc") out = out.slice().sort((a, b) => b.min_price - a.min_price);
    else out = out.slice().sort((a, b) => (a.material_name || "").localeCompare(b.material_name || ""));
    return out;
  }, [grouped, search, category, location, sort]);

  const { pageItems, Pagination } = usePagination(filtered, { perPage: 12 });

  return (
    <div className="min-h-full bg-[#fafaf5]">
      {/* Iter v34b0 — Floating Cart button */}
      <button
        onClick={() => navigate("/client/cart")}
        className="fixed z-40 right-4 md:right-8 bottom-6 md:bottom-8 flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white px-4 py-3 rounded-full shadow-xl shadow-blue-900/30 transition-all"
        data-testid="floating-cart-btn"
      >
        <ShoppingCart className="h-5 w-5"/>
        <span className="text-sm font-medium hidden sm:inline">Keranjang</span>
        {cartCount > 0 && (
          <span className="bg-emerald-400 text-blue-900 rounded-full min-w-[22px] h-[22px] flex items-center justify-center text-[11px] font-bold px-1.5" data-testid="cart-badge">
            {cartCount}
          </span>
        )}
      </button>

      <div className="max-w-[1500px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
        {/* Bento hero */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-8 rounded-3xl bg-gradient-to-br from-blue-700 via-blue-800 to-emerald-800 text-white p-8 md:p-10 relative overflow-hidden">
            <div className="absolute -right-16 -top-16 w-80 h-80 rounded-full bg-emerald-400/20 blur-3xl"></div>
            <div className="absolute right-10 bottom-4 opacity-10">
              <Building2 className="w-40 h-40"/>
            </div>
            <div className="relative max-w-xl">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-emerald-300" />
                <p className="text-[10px] uppercase tracking-[0.3em] text-blue-200 font-semibold">Katalog Material Konstruksi</p>
              </div>
              <h1 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight" data-testid="catalog-hero-title">
                Belanja bahan bangunan <span className="italic text-emerald-300">langsung</span> dari gudang{user?.client_name ? ` untuk ${user.client_name}` : ""}.
              </h1>
              <p className="text-blue-100 mt-3 text-sm">Harga transparan · Termasuk info PPN & biaya bongkar · Hubungi tim CS untuk pengiriman.</p>
              <div className="flex flex-wrap items-center gap-4 mt-5 text-xs uppercase tracking-wider text-blue-200/90">
                <div className="flex items-center gap-1.5"><Package className="h-3 w-3" /> {rows.length} Item</div>
                <div className="flex items-center gap-1.5"><Tag className="h-3 w-3" /> {categories.length} Kategori</div>
                <div className="flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {locations.length} Lokasi</div>
              </div>
            </div>
          </div>

          <div className="col-span-12 md:col-span-4 space-y-4">
            <div className="rounded-2xl bg-white border border-stone-200/70 p-5">
              <MapPin className="w-5 h-5 text-emerald-600 mb-2"/>
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Lokasi tersedia</div>
              <div className="font-medium text-sm mt-1">{locations.length} lokasi pengiriman</div>
              <div className="text-xs text-stone-500 mt-1 line-clamp-2">
                {locations.slice(0, 3).join(", ")}{locations.length > 3 ? `, +${locations.length - 3} lainnya` : ""}
              </div>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 p-5">
              <ShoppingBag className="w-5 h-5 text-emerald-600 mb-2"/>
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Info Pembelian</div>
              <div className="font-medium text-sm mt-1">Hubungi CS untuk pesan</div>
              <div className="text-xs text-stone-600 mt-0.5">Ketuk produk untuk detail harga lengkap & lokasi.</div>
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div className="rounded-2xl bg-white border border-stone-200/70 p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center sticky top-16 z-10 shadow-sm">
          <div className="md:col-span-5 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari material, lokasi, kategori…"
              className="!pl-9"
              data-testid="catalog-search"
            />
          </div>
          <div className="md:col-span-3">
            <Select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="catalog-cat">
              <option value="">Semua Kategori</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </div>
          <div className="md:col-span-2">
            <Select value={location} onChange={(e) => setLocation(e.target.value)} data-testid="catalog-loc">
              <option value="">Semua Lokasi</option>
              {locations.map((l) => <option key={l} value={l}>{l}</option>)}
            </Select>
          </div>
          <div className="md:col-span-2">
            <Select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="catalog-sort">
              <option value="name">Nama A–Z</option>
              <option value="price-asc">Harga Terendah</option>
              <option value="price-desc">Harga Tertinggi</option>
            </Select>
          </div>
        </div>

        {/* Category quick chips */}
        {categories.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <button onClick={() => setCategory("")} className={`px-4 py-1.5 rounded-full text-xs whitespace-nowrap transition ${!category ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-700 hover:border-stone-400"}`}>
              Semua
            </button>
            {categories.map((c) => (
              <button key={c} onClick={() => setCategory(c)}
                className={`px-4 py-1.5 rounded-full text-xs whitespace-nowrap transition ${category === c ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-700 hover:border-stone-400"}`}>
                {c}
              </button>
            ))}
          </div>
        )}

        {/* Bento grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-2xl bg-white border border-stone-200/70 p-4 space-y-3">
                <div className="aspect-[4/3] rounded-xl animate-pulse bg-stone-200/70"></div>
                <div className="h-3 animate-pulse bg-stone-200/70 rounded w-3/4"></div>
                <div className="h-2 animate-pulse bg-stone-200/70 rounded w-1/2"></div>
                <div className="pt-3 border-t border-stone-100 flex items-end justify-between">
                  <div className="space-y-1.5">
                    <div className="h-2 animate-pulse bg-stone-200/70 rounded w-14"></div>
                    <div className="h-5 animate-pulse bg-stone-200/70 rounded w-24"></div>
                  </div>
                  <div className="w-8 h-8 animate-pulse bg-stone-200/70 rounded-full"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-stone-400 rounded-2xl bg-white border border-stone-200/70">
            Tidak ada material yang cocok dengan filter.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {pageItems.map((p, idx) => (
                <ShopBentoCard key={p.key} product={p} stockInfo={stockMap[(p.material_name || "").toLowerCase()]} onClick={() => setActivePrice(p)} highlight={idx === 0} />
              ))}
            </div>
            {Pagination}
          </>
        )}

        {/* Info banner */}
        <div className="rounded-2xl bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-100 p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-6 h-6 text-blue-700"/>
          </div>
          <div className="flex-1">
            <div className="font-medium text-sm text-stone-900">Harga real-time dari gudang RBB</div>
            <div className="text-xs text-stone-600 mt-0.5">
              Semua harga sudah termasuk info PPN 11% terpisah + biaya bongkar per lokasi. Diperbarui otomatis dari sistem stok.
            </div>
          </div>
        </div>
      </div>

      <CatalogDetailModal
        product={activePrice}
        stockInfo={activePrice ? stockMap[(activePrice.material_name || "").toLowerCase()] : null}
        onClose={() => setActivePrice(null)}
      />
    </div>
  );
}

/* ---------- Lazy Image (Iter v34b5) ---------- */
// Cache antar-render agar navigasi antar halaman tidak fetch ulang.
const _imgCache = new Map(); // priceId -> b64 string
function useCatalogImage(priceId, hasImage) {
  const [img, setImg] = React.useState(() => (priceId ? _imgCache.get(priceId) : null) || null);
  React.useEffect(() => {
    if (!priceId || !hasImage) { setImg(null); return; }
    const cached = _imgCache.get(priceId);
    if (cached) { setImg(cached); return; }
    let alive = true;
    api.get(`/prices/${priceId}/image`).then((r) => {
      const b64 = r.data?.image_b64 || null;
      if (b64) _imgCache.set(priceId, b64);
      if (alive) setImg(b64);
    }).catch(() => { if (alive) setImg(null); });
    return () => { alive = false; };
  }, [priceId, hasImage]);
  return img;
}

/* ---------- Bento Shop Card ---------- */
function ShopBentoCard({ product, onClick, highlight, stockInfo }) {
  const variants = product.variants || [];
  const main = product.min_price || 0;
  const initial = (product.material_name || "?").slice(0, 1).toUpperCase();

  // Compute PPN indicator from first variant (respecting visible fields)
  const firstVar = variants[0] || {};
  const vf = Array.isArray(firstVar.catalog_visible_fields) ? firstVar.catalog_visible_fields : [];
  const showAll = vf.length === 0;
  const show = (k) => showAll || vf.includes(k);
  const hasVAT = variants.some((v) => (v.price_with_vat || 0) > 0);

  const colorMap = ["blue", "emerald", "amber", "stone"];
  const color = colorMap[Math.abs(product.key.charCodeAt(0)) % colorMap.length];
  const bgColor = color === "blue" ? "bg-blue-50 text-blue-700"
    : color === "emerald" ? "bg-emerald-50 text-emerald-700"
    : color === "amber" ? "bg-amber-50 text-amber-700"
    : "bg-stone-100 text-stone-700";

  // Iter v34b5 — lazy fetch image (bila product punya has_image)
  const lazyImg = useCatalogImage(product.image_variant_id, product.has_image);

  return (
    <button
      onClick={onClick}
      className={`group text-left rounded-2xl bg-white border border-stone-200/70 hover:border-stone-300 hover:shadow-md transition-all p-4 flex flex-col ${highlight ? "ring-1 ring-blue-200" : ""}`}
      data-testid={`catalog-card-${product.key}`}
    >
      {/* Image / icon */}
      <div className={`aspect-[4/3] rounded-xl overflow-hidden mb-3 relative flex items-center justify-center ${lazyImg ? "" : "bg-gradient-to-br from-blue-50 via-stone-50 to-emerald-50"}`}>
        {lazyImg ? (
          <img src={`data:image/png;base64,${lazyImg}`} alt={product.material_name}
               className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy"/>
        ) : (
          <>
            <Package className="w-16 h-16 text-blue-700/25"/>
            <div className={`absolute bottom-2 left-2 w-9 h-9 rounded-lg flex items-center justify-center ${bgColor} font-bold`}>
              {initial}
            </div>
          </>
        )}
        {product.category && (
          <span className="absolute top-2 left-2 text-[9px] uppercase tracking-wider bg-white/90 backdrop-blur px-2 py-0.5 rounded-full text-stone-700 font-semibold">
            {product.category}
          </span>
        )}
        {/* Iter v34cn — Ganti chip lokasi dgn chip stok tersedia (klien-facing) */}
        {stockInfo && (
          <span className={`absolute top-2 right-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${Number(stockInfo.quantity) > 0 ? "bg-emerald-600/95 text-white" : "bg-red-600/95 text-white"}`} data-testid={`catalog-stock-chip-${product.key}`}>
            Stok: {Math.max(0, Number(stockInfo.quantity) || 0).toLocaleString("id-ID")} {stockInfo.unit || ""}
          </span>
        )}
        {/* Iter v34cn — Stampel STOK HABIS bila qty <= 0 atau tidak tercatat */}
        {(!stockInfo || Number(stockInfo.quantity) <= 0) && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid={`catalog-stock-stamp-${product.key}`}>
            <div className="bg-red-600/85 text-white font-black text-xl md:text-2xl px-4 py-1 border-4 border-red-800 rounded-md rotate-[-12deg] shadow-lg tracking-widest">
              STOK HABIS
            </div>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col">
        <h3 className="font-medium text-sm text-stone-900 leading-tight line-clamp-2 min-h-[2.5rem]">{product.material_name}</h3>
        <p className="text-[10px] text-stone-500 mt-1 uppercase tracking-wider">
          {product.unit && `per ${product.unit}`}
        </p>

        <div className="mt-3 pt-3 border-t border-stone-100 flex items-end justify-between">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-stone-500">
              {variants.length > 1 ? "Mulai dari" : "Harga"}
            </div>
            <div className="font-display text-xl text-blue-800 leading-none">{formatIDR(main)}</div>
            <div className="text-[10px] mt-1">
              {hasVAT ? (
                <span className="text-emerald-700">+ PPN 11%</span>
              ) : (
                <span className="text-stone-500">Non PPN</span>
              )}
            </div>
          </div>
          <div className="w-8 h-8 rounded-full bg-stone-900 text-white flex items-center justify-center group-hover:bg-blue-700 transition">
            <ArrowRight className="w-3.5 h-3.5"/>
          </div>
        </div>
      </div>
    </button>
  );
}

/* ---------- Detail Modal (Tambahkan ke Cart) ---------- */
function CatalogDetailModal({ product, stockInfo, onClose }) {
  const { addItem } = useCart();
  const [qty, setQty] = React.useState("1"); // Iter v34b0 — default 1 for add-to-cart
  const [selectedLoc, setSelectedLoc] = React.useState(0);
  React.useEffect(() => { setQty("1"); setSelectedLoc(0); }, [product?.key]);
  const variants = product?.variants || [];
  const price = variants[selectedLoc] || variants[0] || {};

  // Primary displayed price = Non-PPN (as user requested for main card view)
  const primaryPrice = Number(price.price_non_vat) || 0;

  // Iter v34co — previewOngkir/Bongkar tidak lagi dipakai (diganti Stok Tersedia).
  // Info ongkir & bongkar kini hanya muncul di Cart, bukan katalog.
  const previewVariant = variants[selectedLoc] || variants[0] || {};

  // Iter v34b5 — lazy image (prefer variant terpilih bila punya, fallback ke image_variant_id)
  const imgVariantId = previewVariant.has_image ? previewVariant.id : (product?.image_variant_id || null);
  const lazyImg = useCatalogImage(imgVariantId, !!imgVariantId);

  if (!product) return null;

  return (
    <Modal open={!!product} onClose={onClose} title="" size="xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="catalog-detail-modal">
        {/* Image column (left) */}
        <div>
          <div className="aspect-square rounded-2xl bg-gradient-to-br from-blue-50 via-stone-50 to-emerald-50 flex items-center justify-center overflow-hidden relative">
            {lazyImg ? (
              <img src={`data:image/png;base64,${lazyImg}`} alt={product.material_name} className="h-full w-full object-cover"/>
            ) : (
              <>
                <Package className="w-32 h-32 text-blue-700/25"/>
                <div className="absolute font-display text-6xl text-stone-300">{(product.material_name || "?").slice(0, 1).toUpperCase()}</div>
              </>
            )}
            {product.category && (
              <span className="absolute top-3 left-3 text-[10px] uppercase tracking-wider bg-white/90 backdrop-blur px-2 py-0.5 rounded-full text-stone-700 font-semibold">
                {product.category}
              </span>
            )}
          </div>
        </div>

        {/* Info column (right) - name + description + price + estimasi */}
        <div className="space-y-5" data-testid="catalog-detail-info">
          <div>
            <h2 className="font-display text-3xl tracking-tight text-stone-900">{product.material_name}</h2>
            <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-stone-500">
              <span className="flex items-center gap-1"><Tag className="h-3 w-3"/>Satuan: <span className="font-mono-num font-medium text-stone-700">{product.unit}</span></span>
            </div>
            {product.description && (
              <p className="text-sm text-stone-600 mt-3 leading-relaxed whitespace-pre-line">{product.description}</p>
            )}
          </div>

          {/* Simple Price (no label saying "non ppn") */}
          <div className="rounded-2xl border border-stone-200 bg-white p-5">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Harga</div>
            <div className="font-display text-4xl mt-2 text-blue-800 font-mono-num" data-testid="catalog-primary-price">{formatIDR(primaryPrice)}</div>
            <div className="text-[10px] text-stone-500 mt-1">per {product.unit || "unit"}</div>
          </div>

          {/* Iter v34co — Info Stok Tersedia (ganti Ongkos Kirim + Biaya Bongkar) */}
          <div className="rounded-2xl border border-stone-200 bg-stone-50/50 p-4 space-y-3" data-testid="catalog-preview-stock">
              {variants.length > 1 && (
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-stone-500 mb-1.5">Lokasi Pengiriman</label>
                  <select
                    value={selectedLoc}
                    onChange={(e) => setSelectedLoc(Number(e.target.value))}
                    className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    data-testid="preview-loc"
                  >
                    {variants.map((v, i) => (
                      <option key={v.id} value={i}>{v.delivery_location || "Tanpa Lokasi"}</option>
                    ))}
                  </select>
                </div>
              )}
              {(() => {
                const stockQty = Math.max(0, Number(stockInfo?.quantity) || 0);
                const stockUnit = stockInfo?.unit || product.unit || "unit";
                const lowThreshold = Number(stockInfo?.low_threshold) || 0;
                const isEmpty = !stockInfo || stockQty <= 0;
                const isLow = !isEmpty && lowThreshold > 0 && stockQty <= lowThreshold;
                const tone = isEmpty
                  ? { bg: "bg-red-50", border: "border-red-200", label: "text-red-600", num: "text-red-700", hint: "text-red-500" }
                  : isLow
                  ? { bg: "bg-amber-50", border: "border-amber-200", label: "text-amber-700", num: "text-amber-800", hint: "text-amber-600" }
                  : { bg: "bg-emerald-50", border: "border-emerald-200", label: "text-emerald-700", num: "text-emerald-800", hint: "text-emerald-600" };
                return (
                  <div className={`rounded-xl ${tone.bg} border ${tone.border} p-4`} data-testid="preview-stock-card">
                    <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider ${tone.label} font-semibold`}>
                      <Package className="h-3 w-3"/> Stok Tersedia
                    </div>
                    <div className={`font-display text-3xl ${tone.num} font-mono-num mt-1`} data-testid="preview-stock-qty">
                      {isEmpty ? "STOK HABIS" : `${stockQty.toLocaleString("id-ID")} ${stockUnit}`}
                    </div>
                    <div className={`text-[10px] ${tone.hint} mt-0.5`}>
                      {isEmpty
                        ? "Silakan hubungi marketing untuk info restock"
                        : isLow
                        ? `Stok menipis (batas rendah: ${lowThreshold.toLocaleString("id-ID")} ${stockUnit})`
                        : `Sumber: Master Stok Barang`}
                    </div>
                  </div>
                );
              })()}
            </div>

          {/* Iter v34b0 — Qty selector + Tambahkan ke Cart button */}
          <div className="space-y-3">
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600">Jumlah</label>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => setQty(String(Math.max(1, (Number(qty) || 1) - 1)))}
                className="w-11 h-11 rounded-full border border-stone-300 hover:border-blue-500 hover:text-blue-700 flex items-center justify-center transition"
                data-testid="qty-dec"
              >
                <Minus className="h-4 w-4"/>
              </button>
              <input
                type="number"
                min="1"
                value={qty || 1}
                onChange={(e) => setQty(e.target.value)}
                onFocus={(e) => e.target.select()}
                className="w-24 text-center border border-stone-300 rounded-full px-3 py-2.5 text-base font-mono-num focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                data-testid="qty-input"
              />
              <button
                onClick={() => setQty(String((Number(qty) || 1) + 1))}
                className="w-11 h-11 rounded-full border border-stone-300 hover:border-blue-500 hover:text-blue-700 flex items-center justify-center transition"
                data-testid="qty-inc"
              >
                <Plus className="h-4 w-4"/>
              </button>
              <span className="text-xs text-stone-500 ml-1">{product.unit || "unit"}</span>
            </div>
            <button
              onClick={() => {
                const q = Math.max(1, Number(qty) || 1);
                addItem({
                  material_id: product.key,
                  name: product.material_name,
                  // Iter v34b5 — image_variant_id agar ClientCart bisa lazy-load via /prices/{id}/image
                  image_variant_id: product.image_variant_id || null,
                  unit: product.unit,
                  category: product.category,
                  variants: variants,
                }, q);
                toast.success(`${product.material_name} (${q} ${product.unit || "unit"}) ditambahkan ke keranjang`);
                onClose();
              }}
              className="w-full py-3 rounded-full bg-blue-800 hover:bg-blue-900 text-white text-sm font-medium transition flex items-center justify-center gap-2"
              data-testid="catalog-add-to-cart"
            >
              <ShoppingCart className="h-4 w-4"/> Tambahkan ke Cart
            </button>
          </div>

          <p className="text-[10px] text-stone-500 text-center border-t border-stone-100 pt-3">
            Harga di keranjang dapat diatur ulang (lokasi, PPN, bongkar) sebelum konfirmasi ke marketing.
          </p>
        </div>
      </div>
    </Modal>
  );
}

function PriceRow({ label, value, hint, highlight }) {
  const v = Number(value) || 0;
  return (
    <div className={`flex items-center justify-between gap-4 px-4 py-3 ${highlight ? "bg-blue-50/60" : ""}`}>
      <div className="min-w-0">
        <p className={`text-xs uppercase tracking-wider ${highlight ? "text-blue-800 font-semibold" : "text-stone-600 font-medium"}`}>{label}</p>
        {hint && <p className="text-[10px] text-stone-400 mt-0.5">{hint}</p>}
      </div>
      <p className={`font-mono-num ${highlight ? "text-xl font-bold text-blue-800" : "text-sm text-stone-800"}`}>{formatIDR(v)}</p>
    </div>
  );
}

function CostCard({ icon: Icon, label, value, hint }) {
  const v = Number(value) || 0;
  const empty = v === 0;
  return (
    <div className={`rounded-xl border p-3 ${empty ? "border-dashed border-stone-200 bg-stone-50/40" : "border-stone-200 bg-white"}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-3.5 w-3.5 ${empty ? "text-stone-400" : "text-blue-700"}`} />
        <p className="text-[11px] uppercase tracking-wider text-stone-600 font-semibold">{label}</p>
      </div>
      <p className={`font-mono-num ${empty ? "text-sm text-stone-400" : "text-base text-stone-900 font-semibold"}`}>
        {empty ? "Gratis / tidak ada" : formatIDR(v)}
      </p>
      {hint && <p className="text-[10px] text-stone-400 mt-1 leading-snug">{hint}</p>}
    </div>
  );
}
